import { createFormatForDescription as cffd } from '../createFormatForDescription'
import { Explanation, explain, explainOptional } from '../explain/Explanation'
import PaymentChannelFundInstructions from '../types/instructions/PaymentChannelFundInstructions'
import TransactionType from '../types/TransactionType'
import { parseDate } from '../util'

/**
 * Annotate PaymentChannelFund transactions.
 *
 * @property type - The transaction type.
 * @property amount - An explanation of the amount.
 * @property channel - An explanation of the channel.
 * @property expiration - An explanation of the expiration.
 */
export interface PaymentChannelFundAnnotation {
  readonly type: TransactionType.PaymentChannelFund

  readonly amount: Explanation<string>
  readonly channel: Explanation<string>
  readonly expiration?: Explanation<number>
}

/**
 * Takes a transaction object and returns an annotated transaction object.
 * The returned object contains explanations about the various transaction features.
 *
 * @param transactionInstructions - The input transaction.
 * @returns An annotated transaction object detailing the various transaction features.
 */
export function annotate(
  transactionInstructions: PaymentChannelFundInstructions,
): PaymentChannelFundAnnotation {
  const amount = explain(
    transactionInstructions.Amount,
    `Add ${cffd(
      transactionInstructions.Amount,
      'amount',
    )} XRP (in drops) to the channel.`,
  )

  const channel = explain(
    transactionInstructions.Channel,
    `Funds will be added to channel ${cffd(
      transactionInstructions.Channel,
      'channel',
    )}`,
  )

  const expiration = explainOptional(
    transactionInstructions.Expiration,
    (date) =>
      `The channel will now expire on ${cffd(parseDate(date), 'expiration')}`,
  )

  return {
    type: TransactionType.PaymentChannelFund,
    amount,
    channel,
    ...(expiration && { expiration }),
  }
}
